import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserForm, type UserFormValues } from '@/components/users/UserForm';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { createUserRequest } from '@/services/users';
import type { CreateUserBody, CreateUserResponse, UserDetail } from '@/types/users';

interface CreatedReveal {
  readonly user: UserDetail;
  readonly generatedPassword: string;
}

export function UserCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reveal, setReveal] = useState<CreatedReveal | null>(null);

  const createMutation = useMutation<
    CreateUserResponse,
    ApiError,
    { body: CreateUserBody; passwordWasOmitted: boolean }
  >({
    mutationFn: ({ body }) => createUserRequest(body),
    onSuccess: (response, variables) => {
      toast.success(`User '${response.user.fullName}' created`);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      // CR-01 fix: backend now returns `{ user, generatedPassword }`. When the
      // admin omitted the password the server surfaces the real 16-char plaintext
      // exactly ONCE — render it in the reveal dialog. When the admin supplied an
      // explicit password, `generatedPassword` is null and we skip the dialog
      // (the caller already knows the value). T-08-15 / T-08-19: never log.
      if (variables.passwordWasOmitted && response.generatedPassword !== null) {
        setReveal({
          user: response.user,
          generatedPassword: response.generatedPassword,
        });
      } else {
        void navigate(`/users/${String(response.user.id)}`);
      }
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not create user', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('users.create')) {
    return <Navigate to="/users" replace />;
  }

  function handleSubmit(values: UserFormValues): void {
    setErrorMessage(null);
    const passwordWasOmitted = values.password === undefined || values.password === '';
    const body: CreateUserBody = {
      fullName: values.fullName,
      email: values.email,
      employeeId: values.employeeId ?? null,
      phoneNumber: values.phoneNumber ?? null,
      roleId: values.roleId,
      password: passwordWasOmitted ? null : (values.password ?? null),
      sendResetLink: values.sendResetLink === true ? true : false,
      // Both shapes are sent. `siteIds` is authoritative on a current backend;
      // `siteId` carries the primary so a backend that predates multi-site still
      // assigns something rather than dropping the scope entirely.
      siteId: values.siteIds[0] ?? null,
      siteIds: values.siteIds,
      workOrderIds: values.workOrderIds,
    };
    createMutation.mutate({ body, passwordWasOmitted });
  }

  function handleCloseReveal(): void {
    if (reveal === null) return;
    const id = reveal.user.id;
    setReveal(null);
    void navigate(`/users/${String(id)}`);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create User</h1>
        <p className="text-muted-foreground">Add a new user and assign them a role.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New user</CardTitle>
          <CardDescription>
            Profile fields, role, and an optional initial password (otherwise system-generated).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserForm
            mode="create"
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        </CardContent>
      </Card>

      <AlertDialog
        open={reveal !== null}
        onOpenChange={(open) => {
          if (!open) handleCloseReveal();
        }}
      >
        <AlertDialogContent data-testid="user-created-reveal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>User created</AlertDialogTitle>
            <AlertDialogDescription>
              Copy the temporary password below and deliver it to the user via a secure
              side-channel. It will not be shown again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reveal !== null && (
            <Alert variant="success" data-testid="user-created-reveal-alert">
              <AlertTitle>One-time temporary password</AlertTitle>
              <AlertDescription>
                <code
                  className="font-mono text-sm break-all rounded bg-muted px-2 py-1"
                  data-testid="user-created-generated-password"
                >
                  {reveal.generatedPassword}
                </code>
                <p className="text-xs text-muted-foreground">
                  The user must change this password on first login.
                </p>
              </AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <Button
              type="button"
              onClick={handleCloseReveal}
              data-testid="user-created-reveal-done"
            >
              Done
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
